import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedLayout from '../components/layout/AuthenticatedLayout';
import { authService } from '../services/authService';

const OnboardingPreferences = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    childClientsPreferences: '',
    adultClientsPreferences: '',
    familyReferencesPreferences: '',
    professionalClientsPreferences: '',
    clientConfidentialityPreferences: ''
  });
  const [errors, setErrors] = useState({});

  // Load saved data from backend API and localStorage on component mount and when component becomes visible
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        // First, try to load from backend API
        console.log('Fetching preferences data from backend...');
        const response = await authService.getPreferences();

        if (response && response.status && response.data) {
          console.log('Loading preferences data from backend:', response.data);
          setFormData(response.data);
          // Also save to localStorage as backup
          localStorage.setItem('onboardingPreferences', JSON.stringify(response.data));
          return;
        }
      } catch (err) {
        console.log('Backend fetch failed, loading from localStorage:', err.message);
      }

      // Fallback to localStorage if backend fails
      const savedData = localStorage.getItem('onboardingPreferences');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          console.log('Loading saved preferences data from localStorage:', parsedData);
          setFormData(parsedData);
        } catch (err) {
          console.error('Error loading saved preferences:', err);
        }
      } else {
        console.log('No saved preferences data found');
      }
    };

    // Load data on mount
    loadSavedData();

    // Also load data when component becomes visible (handles back navigation)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadSavedData();
      }
    };

    // Also reload data when window gains focus (handles back button navigation)
    const handleFocus = () => {
      loadSavedData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const handleChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    // Save to localStorage immediately
    localStorage.setItem('onboardingPreferences', JSON.stringify(newFormData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({}); // Clear previous errors

    try {
      console.log('Submitting preferences:', formData);

      // Submit preferences (they can be empty strings, backend will handle it)
      const response = await authService.submitPreferences(formData);
      console.log('Preferences response:', response);

      if (response && (response.status || response.success)) {
        console.log('Preferences saved successfully. Navigating to dashboard...');

        // Show success message
        setShowSuccess(true);

        // Update user object in localStorage to reflect completed onboarding
        const user = authService.getCurrentUser();
        if (user) {
          user.hasCompletedOnboarding = true;
          user.isSubscriptionComplete = true;
          user.hasCompletedPayment = true; // Mark payment as complete to prevent redirect to pricing
          user.onboardingStep = 5;
          localStorage.setItem('user', JSON.stringify(user));
        }

        // Clear onboarding localStorage after successful submission
        localStorage.removeItem('onboardingOrganization');
        localStorage.removeItem('onboardingPersonal');
        localStorage.removeItem('onboardingPreferences');

        // Wait 2 seconds to show success message, then navigate
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        console.error('Failed to save preferences:', response);
        setErrors({ general: response?.message || 'Failed to save preferences.' });
      }
    } catch (err) {
      console.error('Preferences submission error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save. Please try again.';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const PreferenceSection = ({ title, description, field, options }) => (
    <div className="space-y-4 pb-6 border-b border-gray-200 last:border-b-0 last:pb-0">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <div className="space-y-3">
        {options.map((option) => (
          <label
            key={option.value}
            className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
              formData[field] === option.value
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                : 'border-gray-300 bg-white hover:border-blue-300 hover:bg-gray-50'
            }`}
          >
            <input
              type="radio"
              name={field}
              id={option.value}
              value={option.value}
              checked={formData[field] === option.value}
              onChange={(e) => handleChange(field, e.target.value)}
              className="flex-shrink-0 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-3 text-sm text-gray-900 flex-1">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <AuthenticatedLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Progress Indicator */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium">
                  3/3
                </div>
                <div className="w-64 bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '100%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
            {/* Left Column - Form (60%) */}
            <div className="lg:col-span-6 bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="mb-8">
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                  3. Set Your Preferences
                </h1>
                <p className="text-gray-600">
                  Configure how patient references and report structures should be formatted
                </p>
              </div>

              {showSuccess && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">Setup Complete!</h3>
                      <p className="text-sm text-green-700 mt-1">Your preferences have been saved successfully. Redirecting to dashboard...</p>
                    </div>
                  </div>
                </div>
              )}

              {errors.general && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{errors.general}</p>
                    </div>
                    <div className="ml-auto pl-3">
                      <button
                        onClick={() => setErrors({})}
                        className="inline-flex text-red-400 hover:text-red-600"
                      >
                        <span className="sr-only">Dismiss</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Preferences Sections */}
                <div className="space-y-6">
                  <PreferenceSection
                    title="How do you refer to child clients?"
                    description="For example: 'Jane tends to be...' OR 'J tends to be...' OR 'the child tends to be...'"
                    field="childClientsPreferences"
                    options={[
                      { value: 'use formal child name', label: 'Use formal child name, for example "John"' },
                      { value: 'use informal child name', label: 'Use informal child name, for example "Jon"' },
                      { value: 'use first name initial', label: 'Use first name initial "J"' }
                    ]}
                  />

                  <PreferenceSection
                    title="How do you refer to adult clients?"
                    description="Choose how adult client names appear in your reports and documentation"
                    field="adultClientsPreferences"
                    options={[
                      { value: 'use formal adult name', label: 'Use formal adult name, for example "John"' },
                      { value: 'use informal adult name', label: 'Use informal adult name, for example "Jon"' },
                      { value: 'use first name initial', label: 'Use first name initial "J"' }
                    ]}
                  />

                  <PreferenceSection
                    title="How do you refer to family references?"
                    description="Select your preferred terminology when mentioning family in reports"
                    field="familyReferencesPreferences"
                    options={[
                      { value: 'the family', label: 'The family' },
                      { value: 'substitute system', label: 'Substitute "the family" with "the family system"' },
                      { value: 'patients parents', label: 'The patient\'s parents' }
                    ]}
                  />

                  <PreferenceSection
                    title="How do you refer to professional clients?"
                    description="Choose terminology for referring to other healthcare professionals"
                    field="professionalClientsPreferences"
                    options={[
                      { value: 'treating professional', label: 'Treating professional' },
                      { value: 'parents children', label: 'Parents, children, etc.' }
                    ]}
                  />

                  <PreferenceSection
                    title="When discussing a client confidentially, use:"
                    description="Select how to reference clients when maintaining confidentiality"
                    field="clientConfidentialityPreferences"
                    options={[
                      { value: 'patient John 1 said that...', label: 'Patient John 1 said that...' },
                      { value: 'parent 1 said that...', label: 'Parent 1 said that...' },
                      { value: 'Jane (from 1 of 4)', label: 'Jane (from 1 of 4) said that...' }
                    ]}
                  />
                </div>

                {/* Form Actions */}
                <div className="pt-6 border-t border-gray-200 flex justify-between">
                  <button
                    type="button"
                    onClick={() => navigate('/onboarding/personal')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Completing Setup...
                      </>
                    ) : (
                      <>
                        Complete Setup
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column - Info Card (40%) */}
            <div className="lg:col-span-4 lg:sticky lg:top-8">
              <div className="bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl p-8 text-white relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }}></div>
                </div>

                <div className="relative z-10">
                  <h3 className="text-2xl font-bold mb-6 leading-tight">
                    Customize Your Workflow
                  </h3>

                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-300 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">Personalized Report Formatting</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-300 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">Consistent Professional Documentation</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-green-300 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">Flexible Settings You Can Change Anytime</span>
                    </li>
                  </ul>

                  {/* Decorative Element */}
                  <div className="mt-8 flex justify-center">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default OnboardingPreferences;
